import React from 'react';
import { 
  History, Trash2, Calendar, FileText, Globe, 
  Layers, ArrowRight, Shield, Database, ExternalLink 
} from 'lucide-react';
import { ResearchSession } from '../types';

interface ArchiveViewProps {
  sessions: ResearchSession[];
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string, e: React.MouseEvent) => void;
  onClearAll: () => void;
  language: 'uk' | 'en' | 'pl';
}

export default function ArchiveView({
  sessions,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  onClearAll,
  language
}: ArchiveViewProps) {
  
  const getFocusLabel = (focus: string) => {
    const labels: Record<string, { uk: string; en: string }> = {
      general: { uk: 'Загальний аналіз', en: 'General Analysis' },
      science: { uk: 'Наука та інновації', en: 'Science & Tech' },
      finance: { uk: 'Фінанси та ринки', en: 'Finance & Markets' },
      legal: { uk: 'Юриспруденція', en: 'Legal & Policy' }
    };
    return labels[focus]?.[language] || focus;
  };

  const getFormatLabel = (format: string) => {
    const labels: Record<string, { uk: string; en: string }> = {
      comprehensive: { uk: 'Вичерпний звіт', en: 'Comprehensive Report' },
      swot: { uk: 'SWOT-аналіз', en: 'SWOT Analysis' },
      comparison: { uk: 'Порівняльний аналіз', en: 'Comparison Matrix' },
      brief: { uk: 'Аналітична довідка', en: 'Executive Briefing' }
    };
    return labels[format]?.[language] || format;
  };

  return (
    <div className="space-y-6">
      {/* Tab Header with Stats and Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 rounded-lg p-4 shadow-xs">
        <div className="space-y-1">
          <h2 className="text-xs font-bold font-mono text-slate-800 uppercase tracking-widest flex items-center gap-2">
            <History className="w-4 h-4 text-red-500 animate-pulse" />
            <span>{language === 'uk' ? 'СИСТЕМНИЙ АРХІВ ДОСЛІДЖЕНЬ' : 'SYSTEM INTELLIGENCE ARCHIVE'}</span>
          </h2>
          <p className="text-[10px] text-slate-500 font-mono">
            {language === 'uk' 
              ? 'Локально збережені сесії аналізу, доступні для миттєвого відновлення' 
              : 'Locally cached intelligence briefings ready for offline lookup'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-red-50 border border-red-200 text-red-500 text-xs font-mono px-3 py-1 rounded flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5" />
            <span>{sessions.length} {language === 'uk' ? 'ЗВІТІВ' : 'REPORTS'}</span>
          </div>

          {sessions.length > 0 && (
            <button
              onClick={onClearAll}
              className="text-[10px] font-mono font-bold uppercase text-red-600 hover:text-red-700 transition-colors cursor-pointer flex items-center gap-1.5 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded border border-red-200"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {language === 'uk' ? 'Очистити весь архів' : 'Purge All Records'}
            </button>
          )}
        </div>
      </div>

      {sessions.length === 0 ? (
        /* Empty State */
        <div className="border border-dashed border-slate-200 rounded-lg p-12 text-center bg-slate-50 flex flex-col items-center justify-center space-y-4">
          <div className="p-4 bg-white border border-slate-200 rounded-full text-slate-500 shadow-xs">
            <FileText className="w-8 h-8" />
          </div>
          <div className="space-y-1">
            <h3 className="text-xs font-bold font-mono text-slate-600 uppercase tracking-widest">
              {language === 'uk' ? 'АРХІВ ПОВНІСТЮ ПОРОЖНІЙ' : 'ARCHIVE DATABASE EMPTY'}
            </h3>
            <p className="text-[10px] text-slate-500 max-w-md mx-auto leading-relaxed font-mono">
              {language === 'uk' 
                ? 'У вас ще немає збережених звітів. Будь ласка, запустіть нове дослідження в розділі "Координатор пошуку" або імпортуйте дані в аналізатор.'
                : 'No historical briefings found in local storage. Initiate a research query or upload files to populate the database.'}
            </p>
          </div>
        </div>
      ) : (
        /* Grid of Saved Sessions */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sessions.map((sess) => {
            const isActive = sess.id === activeSessionId;
            const totalSources = sess.report?.sources?.length || 
              sess.steps.reduce((acc, step) => acc + (step.payload?.sources?.length || 0), 0);
            const focusVal = sess.options.focus || 'general';
            const formatVal = sess.options.format || 'comprehensive';

            return (
              <div
                key={sess.id}
                onClick={() => onSelectSession(sess.id)}
                className={`group relative bg-white border rounded-lg p-5 flex flex-col justify-between transition-all duration-200 cursor-pointer shadow-xs hover:border-slate-300 ${
                  isActive
                    ? 'border-red-500/50 bg-red-50/30'
                    : 'border-slate-200'
                }`}
              >
                {/* Active Indicator Bar */}
                {isActive && (
                  <div className="absolute left-0 top-3 bottom-3 w-1 bg-red-500 rounded-r shadow-xs" />
                )}

                <div className="space-y-3">
                  {/* Card Header Metadata */}
                  <div className="flex items-center justify-between text-[9px] font-mono text-slate-500">
                    <span className="flex items-center gap-1 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">
                      <Calendar className="w-2.5 h-2.5 text-red-500" />
                      {new Date(sess.createdAt).toLocaleString(language === 'uk' ? 'uk-UA' : 'en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>

                    <span className="flex items-center gap-1 uppercase">
                      <Globe className="w-2.5 h-2.5 text-red-500" />
                      {sess.options.language === 'uk' ? 'UK_UA' : 'EN_US'}
                    </span>
                  </div>

                  {/* Query Title */}
                  <div className="space-y-1 min-w-0">
                    <h3 className="text-xs font-bold font-mono text-slate-400 uppercase tracking-wide">
                      {language === 'uk' ? 'Запит дослідження' : 'Research Query'}
                    </h3>
                    <p className="text-[12px] font-semibold text-slate-850 line-clamp-2 leading-relaxed group-hover:text-red-500 transition-colors">
                      {sess.query}
                    </p>
                  </div>

                  {/* Extended Options Badges */}
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    {/* Depth Badge */}
                    <span className={`text-[8px] font-bold font-mono uppercase px-2 py-0.5 rounded border ${
                      sess.options.depth === 'max'
                        ? 'bg-red-50 text-red-500 border-red-200'
                        : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>
                      {sess.options.depth === 'max' 
                        ? (language === 'uk' ? 'ГЛИБОКИЙ' : 'DEEP DEPTH') 
                        : (language === 'uk' ? 'СТАНДАРТНИЙ' : 'STANDARD')}
                    </span>

                    {/* Focus Sector Badge */}
                    <span className="text-[8px] font-bold font-mono uppercase px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">
                      {getFocusLabel(focusVal)}
                    </span>

                    {/* Format / Style Badge */}
                    <span className="text-[8px] font-bold font-mono uppercase px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">
                      {getFormatLabel(formatVal)}
                    </span>

                    {/* Sources Count Badge */}
                    {totalSources > 0 && (
                      <span className="text-[8px] font-bold font-mono uppercase px-2 py-0.5 rounded border bg-red-50 text-red-500 border-red-200">
                        {totalSources} {language === 'uk' ? 'ДЖЕРЕЛ' : 'SOURCES'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Bottom Card Actions */}
                <div className="flex items-center justify-between border-t border-slate-200 pt-3.5 mt-4">
                  <span className="text-[10px] font-mono font-bold text-red-500 flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                    {language === 'uk' ? 'ВІДКРИТИ ЗВІТ' : 'VIEW DISPATCH'}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSession(sess.id, e);
                    }}
                    className="p-1.5 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded transition-all cursor-pointer"
                    title={language === 'uk' ? 'Видалити звіт з архіву' : 'Delete briefing from cache'}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
